/* Native cart drawer — STEP 1: SHELL ONLY.
 *
 * Open/close, focus trap, escape-to-close, outside-click-close and an
 * iOS-safe scroll lock. Deliberately knows nothing about cart contents —
 * no fetching, no line items, no membership integration. That is step 2.
 *
 * Only loaded when settings.cart_type == 'drawer'; with the default
 * ('notification') this file is never requested.
 *
 * Reuses the theme's own trapFocus / removeTrapFocus from global.js so
 * focus behaviour matches the menu drawer and the cart notification.
 */
if (!customElements.get('cart-drawer')) {
  customElements.define(
    'cart-drawer',
    class CartDrawer extends HTMLElement {
      constructor() {
        super();
        this.panel = this.querySelector('[data-cart-drawer-panel]');
        this.overlay = this.querySelector('[data-cart-drawer-overlay]');
        this.closeButton = this.querySelector('[data-cart-drawer-close]');
        this.opener = null;
        this.scrollY = 0;

        this.onKeydown = this.onKeydown.bind(this);
        this.close = this.close.bind(this);
      }

      connectedCallback() {
        if (this.closeButton) this.closeButton.addEventListener('click', this.close);
        if (this.overlay) this.overlay.addEventListener('click', this.close);
        this.bindContents();
        this.installCartObserver();

        // The membership orchestrator bundles this drawer's section into its own
        // auto-add/auto-remove request and swaps it directly (refreshTargets).
        // When it does, cancel the refetch our observer already queued — without
        // this the drawer repaints twice for every membership action.
        var self = this;
        document.addEventListener('society:cart-repainted', function () {
          clearTimeout(self._refreshTimer);
        });

        // The cart icon is a plain link to /cart. Intercept it so the drawer
        // opens instead, but leave the href intact: no-JS, middle-click and
        // cmd-click all still reach the cart page.
        document.addEventListener('click', function (event) {
          var trigger = event.target.closest('#cart-icon-bubble, [data-cart-drawer-open]');
          if (!trigger) return;
          // Let modified clicks (new tab / new window / download) behave normally.
          if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
          event.preventDefault();
          this.open(trigger);
        }.bind(this));
      }

      get isOpen() {
        return this.hasAttribute('open');
      }

      /* ---------------- cart contents (step 2) --------------------------
       * Quantity and remove go through /cart/change.js with the drawer's own
       * section bundled in, then the returned .js-contents is swapped in —
       * the same Section Rendering pattern assets/cart.js uses for the cart
       * page. Listeners are delegated off the drawer root, so they survive
       * every swap without rebinding.
       */
      bindContents() {
        var self = this;

        this.addEventListener('click', function (event) {
          var line = event.target.closest('[data-cart-drawer-line]');
          if (!line) return;
          var key = line.getAttribute('data-cart-drawer-line');
          var input = line.querySelector('[data-cart-drawer-qty-input]');
          var current = input ? parseInt(input.value, 10) || 0 : 0;

          if (event.target.closest('[data-cart-drawer-remove]')) {
            event.preventDefault();
            self.changeLine(key, 0);
          } else if (event.target.closest('[data-cart-drawer-plus]')) {
            self.changeLine(key, current + 1);
          } else if (event.target.closest('[data-cart-drawer-minus]')) {
            // 0 removes the line, which matches the cart page's behaviour.
            self.changeLine(key, Math.max(0, current - 1));
          }
        });

        this.addEventListener('change', function (event) {
          var input = event.target.closest('[data-cart-drawer-qty-input]');
          if (!input) return;
          var line = input.closest('[data-cart-drawer-line]');
          if (!line) return;
          var qty = parseInt(input.value, 10);
          if (isNaN(qty) || qty < 0) qty = 0;
          self.changeLine(line.getAttribute('data-cart-drawer-line'), qty);
        });
      }

      get contents() {
        return this.querySelector('[data-cart-drawer-contents]');
      }

      /* The drawer is rendered server-side at page load, so it holds the cart
       * as it was THEN. Anything else that changes the cart — the product form,
       * the membership orchestrator's auto-add, another app — leaves the drawer
       * showing stale (often empty) contents until a reload.
       *
       * So observe every cart mutation, whoever makes it, and re-read the
       * drawer section. Wrapper is pass-through: original return value and
       * rejections are untouched, the body is never consumed, and our reaction
       * runs out-of-band so a failure here cannot break the caller.
       */
      installCartObserver() {
        if (window.__cartDrawerObserver) return;
        window.__cartDrawerObserver = true;
        var self = this;

        function isCartMutation(url) {
          try {
            var u = new URL(String(url), window.location.origin);
            if (u.origin !== window.location.origin) return false;
            return /\/cart\/(add|change|update|clear)(\.js)?$/.test(u.pathname);
          } catch (e) { return false; }
        }

        var origFetch = window.fetch;
        if (typeof origFetch === 'function') {
          window.fetch = function () {
            var url = '';
            try {
              var input = arguments[0];
              url = input && typeof input === 'object' && 'url' in input ? input.url : String(input);
            } catch (e) { /* never block the call */ }
            var result = origFetch.apply(this, arguments);
            try {
              if (isCartMutation(url) && result && typeof result.then === 'function') {
                result.then(function () { self.scheduleRefresh(); }, function () { /* caller's error */ });
              }
            } catch (e) { /* observation is best-effort */ }
            return result;
          };
        }

        var proto = window.XMLHttpRequest && window.XMLHttpRequest.prototype;
        if (proto && typeof proto.open === 'function') {
          var origOpen = proto.open;
          var origSend = proto.send;
          proto.open = function (method, url) {
            try { this.__cartDrawerMutation = isCartMutation(url); } catch (e) {}
            return origOpen.apply(this, arguments);
          };
          proto.send = function () {
            try {
              if (this.__cartDrawerMutation) {
                this.addEventListener('loadend', function () { self.scheduleRefresh(); });
              }
            } catch (e) {}
            return origSend.apply(this, arguments);
          };
        }
      }

      scheduleRefresh() {
        // Skip exactly one refresh per mutation WE issued (changeLine already
        // swaps from its own response). Counting rather than using a time
        // window matters: a 200ms blanket window could swallow the refresh for
        // the membership auto-add if the orchestrator acted inside it, leaving
        // the membership in the cart but invisible in the drawer.
        if (this.ownMutations > 0) {
          this.ownMutations--;
          return;
        }
        var self = this;
        clearTimeout(this._refreshTimer);
        this._refreshTimer = setTimeout(function () { self.refreshFromServer(); }, 120);
      }

      // Read-only: no /cart/ path, so it cannot re-trigger the observer.
      refreshFromServer() {
        var self = this;
        return window
          .fetch(((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/') +
                 '?sections=cart-drawer,cart-icon-bubble', { credentials: 'same-origin', cache: 'no-store' })
          .then(function (r) { return r.ok ? r.json() : null; })
          .then(function (sections) { if (sections) self.applySections(sections); })
          .catch(function () { /* a failed repaint must never break the cart */ });
      }

      // Called by product-form.js after an add-to-cart in drawer mode.
      openWithRefresh(opener) {
        var self = this;
        return this.refreshFromServer().then(function () { self.open(opener); });
      }

      setBusy(state) {
        this.classList.toggle('cart-drawer--busy', !!state);
        this.querySelectorAll('[data-cart-drawer-qty-input], [data-cart-drawer-qty] button, [data-cart-drawer-remove]')
          .forEach(function (el) { el.disabled = !!state; });
      }

      showError(message) {
        var el = this.querySelector('[data-cart-drawer-error]');
        if (el) el.textContent = message || ' ';
      }

      changeLine(key, quantity) {
        if (!key || this.busy) return;
        this.busy = true;
        this.ownMutations = (this.ownMutations || 0) + 1; // observer skips this one
        this.setBusy(true);
        this.showError('');

        var self = this;
        var body = JSON.stringify({
          id: key,
          quantity: quantity,
          // The drawer lives on every page, so it must be requested by section
          // name. cart-icon-bubble rides along so the header count cannot go
          // stale after a change made inside the drawer.
          sections: 'cart-drawer,cart-icon-bubble',
          sections_url: window.location.pathname
        });

        window
          .fetch((window.routes && window.routes.cart_change_url) || '/cart/change.js', {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: body
          })
          .then(function (response) {
            return response.json().then(function (data) {
              if (!response.ok) throw new Error(data && data.description ? data.description : 'Cart update failed');
              return data;
            });
          })
          .then(function (data) {
            self.applySections(data.sections);
          })
          .catch(function (err) {
            self.showError((err && err.message) || 'Cart update failed');
          })
          .then(function () {
            self.busy = false;
            self.setBusy(false);
          });
      }

      applySections(sections) {
        if (!sections) return;
        try {
          var html = sections['cart-drawer'];
          if (html && this.contents) {
            var fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-cart-drawer-contents]');
            if (fresh) this.contents.innerHTML = fresh.innerHTML;
          }
          var bubbleHtml = sections['cart-icon-bubble'];
          var bubble = document.getElementById('cart-icon-bubble');
          if (bubbleHtml && bubble) {
            var freshBubble = new DOMParser().parseFromString(bubbleHtml, 'text/html').querySelector('.shopify-section');
            // Never write empty content into the header anchor: base.css carries
            // `a:empty { display: none }`, so an empty write makes the whole cart
            // icon vanish until the next server render.
            if (freshBubble && freshBubble.innerHTML.trim() !== '') {
              bubble.innerHTML = freshBubble.innerHTML;
            }
          }
        } catch (e) { /* a failed repaint must never break the cart */ }
      }

      open(opener) {
        if (this.isOpen) return;
        this.opener = opener || null;
        this.lockScroll();
        this.setAttribute('open', '');
        if (this.opener && this.opener.setAttribute) this.opener.setAttribute('aria-expanded', 'true');
        document.addEventListener('keydown', this.onKeydown);

        // Focus after the opening transition so the browser doesn't scroll the
        // panel while it is still sliding in.
        var focusTarget = this.closeButton || this.panel;
        var self = this;
        var done = function () {
          if (typeof trapFocus === 'function') trapFocus(self.panel, focusTarget);
          else if (focusTarget && focusTarget.focus) focusTarget.focus();
        };
        if (this.panel) {
          this.panel.addEventListener('transitionend', done, { once: true });
          // Fallback in case the transition is skipped (reduced motion, etc.)
          setTimeout(done, 350);
        } else {
          done();
        }
      }

      close() {
        if (!this.isOpen) return;
        this.removeAttribute('open');
        document.removeEventListener('keydown', this.onKeydown);
        this.unlockScroll();
        if (this.opener && this.opener.setAttribute) this.opener.setAttribute('aria-expanded', 'false');

        if (typeof removeTrapFocus === 'function') removeTrapFocus(this.opener);
        else if (this.opener && this.opener.focus) this.opener.focus();
        this.opener = null;
      }

      onKeydown(event) {
        if (event.key === 'Escape' || event.key === 'Esc') {
          event.stopPropagation();
          this.close();
        }
      }

      /* Scroll lock: position-fixed + restore scrollY.
       * `overflow: hidden` on body does not hold on iOS Safari — the page
       * behind still rubber-bands and can scroll away under the drawer. Pinning
       * the body and restoring the offset on close is the reliable approach.
       */
      lockScroll() {
        this.scrollY = window.scrollY || document.documentElement.scrollTop || 0;
        var body = document.body;
        body.style.position = 'fixed';
        body.style.top = '-' + this.scrollY + 'px';
        body.style.left = '0';
        body.style.right = '0';
        body.style.width = '100%';
        body.classList.add('cart-drawer-open');
      }

      unlockScroll() {
        var body = document.body;
        body.style.position = '';
        body.style.top = '';
        body.style.left = '';
        body.style.right = '';
        body.style.width = '';
        body.classList.remove('cart-drawer-open');
        // Restore without smooth-scrolling, which would animate the jump back.
        var behavior = document.documentElement.style.scrollBehavior;
        document.documentElement.style.scrollBehavior = 'auto';
        window.scrollTo(0, this.scrollY || 0);
        document.documentElement.style.scrollBehavior = behavior;
      }
    }
  );
}
